/**
 * Functions for initializing `axios` and the API request environment
 */

import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import _get from 'lodash/get';
import * as Sentry from '@sentry/react';
import { AppName } from 'sl-api-connector/types';
import { initializeMixpanel, track } from 'src/utils/mixpanel';
import { getAppName, isDrive } from 'src/utils/app';

/**
 * URLs that contain these following strings are considered non-mutating (even if the request is not a GET request)
 * and will be retried upon failure.
 */
const whitelistedRetryEndpoints = [
  '/AdvancedSearch',
  '/GetConfig',
  '/GetEntityMetadata',
  '/GetLatestUserUploadRequests',
  '/UploadHighRiskKeywords',
  '/api/beaconcontent/', // POST endpoints like GetContentUtilizationSummary; retry on 500 (e.g. empty body)
  '/apiAdManager/adCampaigns/getTargetKeywords',
  '/apiAdManager/adCampaigns/getTargetProducts',
  '/apiAdManager/adCampaigns/getCampaignProducts',
  '/apiAdManager/keywordRecommendations/getRecommendedKeywords/',
  '/apiAdManager/scheduledActions/getScheduledActionByCampaignId',
  '/apiAdManager/scheduledActions/getScheduledActionByPortfolioId',
  '/apiAdManager/scheduledActions/getScheduledActionByEntityId'
];

const AXIOS_RETRIES = 3;

/** Unique message for Sentry when axios exhausts all retries (search for this in Sentry). */
const AXIOS_RETRY_EXHAUSTED_MESSAGE = '[AxiosRetry] Request failed after maximum retries';

/**
 * Determines if a failed request should be retried. Retry does NOT depend on response body.
 * Known edge cases where 500 with no body can still prevent retries unless handled:
 * - CORS: If OPTIONS preflight fails, browser reports "Network Error" with no error.response; we retry via whitelist when URL is whitelisted.
 * - Timeout: Server 500 + Content-Length:0 with slow/improper connection close can surface as ECONNABORTED (no response); we retry for whitelisted URLs.
 * - Empty body itself is fine: status comes from headers, so 500 + Content-Length:0 is retried via the status check above.
 */
const shouldRetryRequest = (err) => {
  try {
    const url = err.config?.url ?? '';
    const isWhitelisted = whitelistedRetryEndpoints.some((endpoint) => url.includes(endpoint));

    // Check response status from headers only; does not depend on response body (works for 500 + Content-Length: 0).
    const status = err.response?.status;
    if (typeof status === 'number') {
      if (status === 401) {
        return false;
      }
      // Retry on 404 (transient not-found) or any 5xx, regardless of HTTP method or body.
      if (status === 404 || (status >= 500 && status <= 599)) {
        return true;
      }
    }

    // No response received (e.g. connection reset, empty body edge case) but request was sent - retry if whitelisted.
    if (!err.response && err.request && isWhitelisted) {
      return true;
    }

    if (isWhitelisted) {
      return true;
    }

    // Fall back to default: network error or 5xx on idempotent request (GET, HEAD, OPTIONS, PUT, DELETE).
    return isNetworkOrIdempotentRequestError(err);
  } catch {
    // Defensive: if retry logic throws (e.g. unexpected error shape), don't retry to avoid repeated failures.
    return false;
  }
};

/**
 * This function sets up default `axios` functionality such as the base URL for API requests, default headers,
 * request authentication, and retry functionality.  It should be called before any API requests are made by the
 * application.
 *
 * @param {string} baseURL The base URL to use for API requests
 * @param {func} handleServerError A function that will be called with the status code of error response codes from
 *  the server such as 401 and 500. Used to log out on 401.
 */
export const configureAxios = (baseURL, handleServerError) => {
  axios.defaults.baseURL = baseURL;
  axios.defaults.headers.crossDomain = true;
  axios.defaults.withCredentials = true;
  
  // response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Retry read requests that return 204 No Content up to three times, waiting 2.5s between attempts.
      // A counter (__noContentRetries) is stored directly on the config object so it persists across retries.
      if (response.status === 204) {
        const method = ((response.config as any).method || '').toLowerCase();
        if (['get', 'head', 'options'].includes(method)) {
          const retryCount: number = (response.config as any).__noContentRetries || 0;
          if (retryCount < 3) {
            return new Promise<void>((resolve) => setTimeout(resolve, 2.5 * 1000)).then(() => {
              (response.config as any).__noContentRetries = retryCount + 1;
              return axios(response.config);
            });
          }
        }
      }

      if (_get(response, 'config.meta.requestStartedAt')) {
        track('response time', {
          status: response.status,
          duration: new Date().getTime() - (response.config as any).meta.requestStartedAt,
          url: response.config.url
        });
      }
      if (process.env.NODE_ENV !== 'development' && response.config.url.includes('/api/user/GetConfig')) {
        initializeMixpanel(response.data.appConfig.mixpanelApiKey);
      }
      return response;
    },
    (error) => {
      // Log and surface to Sentry when all retries were exhausted (unique message for filtering)
      const retryState = error?.config?.['axios-retry'];
      if (retryState && retryState.retryCount >= AXIOS_RETRIES) {
        const status = error.response?.status ?? 'network_error';
        const url = _get(error, 'config.url') || _get(error, 'config.baseURL', '');
        console.error(
          `${AXIOS_RETRY_EXHAUSTED_MESSAGE} (${status}): ${url}`,
          error
        );
        Sentry.captureMessage(`${AXIOS_RETRY_EXHAUSTED_MESSAGE} ${url}`, 'error');
      }

      if (error.response && error.response.status) {
        const { status } = error.response;
        handleServerError(status);

        // Capture all errors using Sentry for Drive
        if (isDrive) {
          Sentry.captureException(error);
        }

        if (_get(error, 'config.meta.requestStartedAt')) {
          track('response time', {
            status: error.response.status,
            time: new Date().getTime() - error.config.meta.requestStartedAt,
            url: error.config.url
          });
        }
      }
      return Promise.reject(error);
    }
  );

  // Retry requests that fail up to three times, waiting 2.5 seconds in between retries.
  axiosRetry(axios, {
    retries: AXIOS_RETRIES,
    retryCondition: shouldRetryRequest,
    retryDelay: () => 2.5 * 1000
  });

  // rateLimit(axios, { maxRPS: 12 });
};
